import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  defectKeys,
  equipmentKeys,
  inspectionKeys,
  scopeOfWorkKeys,
} from "@/api/cache-keys";
import { getListDefectPhotosQueryKey } from "@/api/client-defect";
import { getListEquipmentBatchUploadsQueryKey } from "@/api/client-equipment";
import {
  getGetScopeOfWorkQueryKey,
  getListInspectionDocumentsQueryKey,
} from "@/api/client-inspection";
import {
  clearSessionCache,
  getCachedSession,
  getJwtToken,
  setJwtToken,
} from "@/lib/auth-client";

export function useGlobalSSE() {
  const queryClient = useQueryClient();

  useEffect(() => {
    let active = true;
    // Aborts the request in flight and the open stream when the effect is
    // cleaned up. Without it, a cleanup that ran while the fetch was still
    // connecting (React StrictMode runs every effect twice in development)
    // left that response open and unread, so each page held two streams.
    const controller = new AbortController();
    let reconnectTimeoutId: ReturnType<typeof setTimeout>;
    const decoder = new TextDecoder();
    let buffer = "";
    let consecutiveFailures = 0;
    // A 401 is answered by refreshing the token and retrying at once. That is
    // right when the token simply expired, and wrong when the API is rejecting
    // every token — during an auth-svc restart the refresh keeps succeeding, so
    // without this counter the retry never backs off. One outage produced 904
    // stream requests and 995 session refreshes in 92 seconds.
    let authRefreshes = 0;
    const MAX_AUTH_REFRESHES = 1;

    const retryWithBackoff = () => {
      consecutiveFailures++;
      const delay = Math.min(5000 * 2 ** (consecutiveFailures - 1), 60000);
      if (active) reconnectTimeoutId = setTimeout(connectAndRead, delay);
      return delay;
    };

    const connectAndRead = async () => {
      const token = getJwtToken();

      if (!token) return;

      try {
        const response = await fetch("/api/sse/stream", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
          signal: controller.signal,
        });

        if (response.status === 401) {
          // Token expired — bust the session cache and fetch a fresh JWT.
          setJwtToken(null);
          clearSessionCache();
          const session = await getCachedSession();
          if (!session.data || !getJwtToken()) {
            // No valid session — user is logged out. Stop retrying.
            return;
          }
          if (authRefreshes >= MAX_AUTH_REFRESHES) {
            // The API rejected a token that was just issued, so the problem is
            // not an expired token. Back off rather than refresh again.
            const delay = retryWithBackoff();
            console.warn(
              "SSE unauthorized with a fresh token, retrying in %dms",
              delay,
            );
            return;
          }
          authRefreshes++;
          // Got a fresh token — retry immediately, once.
          if (active) reconnectTimeoutId = setTimeout(connectAndRead, 0);
          return;
        }

        if (!response.ok || !response.body) {
          throw new Error(`SSE Connection failed: ${response.status}`);
        }

        const reader = response.body.getReader();
        // The stream is open, so both budgets refill.
        consecutiveFailures = 0;
        authRefreshes = 0;

        // `active` is toggled false by the effect cleanup; reads are sequential by design.
        /* eslint-disable no-await-in-loop, no-unmodified-loop-condition */
        while (active) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          let eolIndex = buffer.indexOf("\n\n");
          while (eolIndex !== -1) {
            const message = buffer.slice(0, eolIndex);
            buffer = buffer.slice(eolIndex + 2);
            eolIndex = buffer.indexOf("\n\n");

            const lines = message.split("\n");
            let eventType = "message";
            let eventData = "";

            for (const line of lines) {
              if (line.startsWith("event:")) {
                eventType = line.slice(6).trim();
              } else if (line.startsWith("data:")) {
                eventData = line.slice(5).trim();
              }
            }

            if (eventData) {
              handleSseEvent(eventType, eventData);
            }
          }
        }
        /* eslint-enable no-await-in-loop, no-unmodified-loop-condition */

        // Stream ended cleanly — reset backoff and reconnect.
        consecutiveFailures = 0;
        if (active) reconnectTimeoutId = setTimeout(connectAndRead, 1000);
      } catch (err) {
        if (active) {
          const delay = retryWithBackoff();
          console.error("SSE Error, retrying in %dms:", delay, err);
        }
      }
    };

    // Default refetchType is "active" — currently-rendered queries refetch
    // immediately so the user sees the change without leaving the page; any
    // inactive queries are simply marked stale and refetch on next visit.
    // This avoids hammering the API with refetches for screens nobody is
    // looking at, while still giving live-update behavior on the open page.
    const invalidateKeys = (keys: readonly (readonly unknown[])[]) => {
      for (const key of keys) {
        queryClient.invalidateQueries({ queryKey: key as unknown[] });
      }
    };

    const handleSseEvent = (type: string, rawData: string) => {
      // Parse lightweight payload for IDs
      let data: Record<string, string> = {};
      try {
        data = JSON.parse(rawData);
      } catch {
        // ignore parse errors
      }

      // ── Equipment events ──
      if (
        type.startsWith("equipment.created") ||
        type.startsWith("equipment.updated") ||
        type.startsWith("equipment.deleted") ||
        type === "equipment.import.requested" ||
        type === "equipment.import.completed"
      ) {
        queryClient.invalidateQueries({ queryKey: ["equipment-uploads"] });
        queryClient.invalidateQueries({
          queryKey: getListEquipmentBatchUploadsQueryKey(),
        });

        // Invalidate equipment (both infinite and non-infinite queries)
        invalidateKeys(equipmentKeys.lists());
      }

      // ── SOW membership events (from equipment-svc) ──
      if (type === "equipment.scope_of_work.updated") {
        invalidateKeys(equipmentKeys.lists());
      }

      // ── IECEx events (from iecex-svc) ──
      // A lookup that fails changes what the equipment page says about a
      // missing certificate — from "fetching" to why it never arrived. Only
      // the failure needs this: a certificate that resolves arrives as a
      // document, and that path already refreshes the page.
      //
      // Invalidated by predicate rather than by key: the event names a
      // certificate, not a tag, and one certificate legitimately covers a
      // hundred of them. Equipment detail keys are full paths with no shared
      // prefix to match on, so the alternative would be re-deriving the covered
      // group in the browser from data it does not have.
      if (type === "iecex.certificate.unresolved") {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const [key] = query.queryKey;
            return typeof key === "string" && key.startsWith("/api/equipment/");
          },
        });
      }

      // ── Inspection / document events ──
      // When a PDF is generated and uploaded, refetch document lists
      // so download buttons become active without manual refresh.
      if (
        type === "equipment.inspection_sheet.ready" ||
        type === "document.uploaded"
      ) {
        if (data.scopeOfWorkId) {
          queryClient.invalidateQueries({
            queryKey: getGetScopeOfWorkQueryKey(data.scopeOfWorkId),
          });
        }
        // An inspection's own files (sheet, attachments) name the inspection
        // as their owner. No query used the old ["inspection-documents"] key,
        // so this refresh never reached the Documents card.
        if (data.ownerId && data.purpose?.startsWith("inspection_")) {
          queryClient.invalidateQueries({
            queryKey: getListInspectionDocumentsQueryKey(data.ownerId),
          });
        }
      }

      // Defect photo gallery — invalidate when a defect_photo document is
      // uploaded. ownerId on the event is the defect ID. Cross-link /
      // unlink no longer flow through document.* events — they're owned by
      // defect-svc and the iPlant mutation invalidates the gallery directly.
      if (
        type === "document.uploaded" &&
        data.purpose === "defect_photo" &&
        data.ownerId
      ) {
        queryClient.invalidateQueries({
          queryKey: getListDefectPhotosQueryKey(data.ownerId),
        });
      }

      // ── Inspection events (from inspection-svc) ──
      if (
        type === "inspection.created" ||
        type === "inspection.updated" ||
        type === "inspection.completed"
      ) {
        invalidateKeys(inspectionKeys.lists());
        invalidateKeys(inspectionKeys.summary());
        if (data.id) {
          invalidateKeys(inspectionKeys.detail(data.id));
        }
      }

      // ── Scope of Work events (from inspection-svc) ──
      if (type === "inspection.scope_of_work.created") {
        invalidateKeys(scopeOfWorkKeys.lists());
      }

      if (
        type === "inspection.scope_of_work.completed" ||
        type === "inspection.scope_of_work.cancelled" ||
        type === "inspection.scope_of_work.scheduled" ||
        type === "inspection.scope_of_work.started"
      ) {
        invalidateKeys(scopeOfWorkKeys.lists());
        if (data.id) {
          invalidateKeys(scopeOfWorkKeys.detail(data.id));
        }
        // Child inspections may have changed status
        invalidateKeys(inspectionKeys.lists());
        invalidateKeys(inspectionKeys.summary());
      }

      if (
        type === "inspection.scope_of_work.equipment_added" ||
        type === "inspection.scope_of_work.equipment_removed"
      ) {
        if (data.scopeOfWorkId) {
          invalidateKeys(scopeOfWorkKeys.detail(data.scopeOfWorkId));
        }
        // Equipment table renders a "Scopes of work" column, so the list
        // needs to refetch. Equipment-svc also republishes
        // equipment.scope_of_work.updated after it processes its own
        // membership read-model — that handler invalidates the list a
        // second time, which catches any race where the first refetch
        // beat equipment-svc to the punch.
        invalidateKeys(equipmentKeys.lists());
        if (data.equipmentId) {
          invalidateKeys(equipmentKeys.detail(data.equipmentId));
        }
        // Adding equipment to a SOW creates a new inspection per item;
        // removing one cancels its inspection. Either way, inspection
        // lists need to refresh.
        invalidateKeys(inspectionKeys.lists());
        invalidateKeys(inspectionKeys.summary());
      }

      // ── Defect events (from defect-svc) ──
      if (
        type === "defect.created" ||
        type === "defect.deleted" ||
        type === "defect.status.updated" ||
        type === "defect.updated"
      ) {
        invalidateKeys(defectKeys.lists());
        invalidateKeys(defectKeys.summary());
        if (data.id) {
          invalidateKeys(defectKeys.detail(data.id));
          // The audit trail captures every defect.updated and
          // defect.status.updated, so refresh it whenever the defect changes.
          invalidateKeys(defectKeys.audit(data.id));
        }
        // Defects are linked to inspections — invalidate the parent inspection
        // detail if available, otherwise invalidate inspection lists so defect
        // counts on inspection rows refresh.
        if (data.inspectionId) {
          invalidateKeys(inspectionKeys.detail(data.inspectionId));
        } else {
          invalidateKeys(inspectionKeys.lists());
        }
      }

      if (type === "defect.corrective_action.added") {
        if (data.defectId) {
          invalidateKeys(defectKeys.detail(data.defectId));
        }
      }
    };

    connectAndRead();

    return () => {
      active = false;
      clearTimeout(reconnectTimeoutId);
      controller.abort();
    };
  }, [queryClient]);
}
